/**
 * Utilities for cleaning and preparing data before writing to Cloud Firestore.
 * Firestore rejects documents containing `undefined` values.
 */

/**
 * Deeply sanitizes data for Cloud Firestore by removing all properties whose value is `undefined`.
 * Preserves null, primitives, arrays, and nested objects.
 */
export function sanitizeForFirestore<T>(data: T): T {
  if (data === undefined) {
    return null as unknown as T;
  }

  if (data === null || typeof data !== "object") {
    return data;
  }

  // Handle Dates
  if (data instanceof Date) {
    return data;
  }

  // Handle Arrays recursively
  if (Array.isArray(data)) {
    return data
      .filter((item) => item !== undefined)
      .map((item) => sanitizeForFirestore(item)) as unknown as T;
  }

  // Handle plain Objects
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (value !== undefined) {
      result[key] = sanitizeForFirestore(value);
    }
  }

  return result as T;
}
